import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { IImportRowError, IImportRowsResponse, TableEditorService } from "../../services/table-editor-service";
import { KModal } from "../ui-kit/k-modal/k-modal";
import "../ui-kit/k-modal/k-modal-footer.scss";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaRadioGroup, { KuikaRadioGroupOption } from "../ui-kit/kuika-radio-group/kuika-radio-group";
import TableEditorAlert from "./table-editor-alert";
import { buildTableEditorErrorAlert, createTableEditorAlert, ITableEditorAlert } from "./table-editor-errors";

// KOA-5857 — "Import rows from CSV / XLSX" modal.
//
// Thin wrapper around the server-side streaming import endpoint. The file
// goes straight to the backend (multipart upload), which streams the parse,
// runs each row through its own persister call (per-row best-effort, matching
// the interactive editor's batch contract), and returns a summary. No
// client-side parse, no client-side row aggregation — works against tables
// of any size that the upload size limit accommodates.

// Outcome surfaced to the parent after the server returns. Mirrors
// IImportRowsResponse's user-facing shape minus the per-row errors array —
// the parent only needs the headline numbers + warnings for its notice banner.
export interface IImportSummary {
  inserted: number;
  failed: number;
  warnings: string[];
}

export type ImportMode = "insert" | "upsert";

// Summary-line verb. Upsert mode's count is insert + update combined — the
// DB path doesn't separate them on a per-row basis — so "inserted" would be
// a lie when even one row resolved to an UPDATE. "Saved" is the neutral
// phrasing; the insert-only path keeps "inserted" because there it IS the
// truth. Dry-run says "would X" regardless.
const summaryVerb = (mode: ImportMode, isDryRun: boolean): string => {
  if (mode === "upsert") {
    return isDryRun ? "would save" : "saved";
  }
  return isDryRun ? "would insert" : "inserted";
};

// Static — doesn't depend on props/state — so it's a module constant rather than rebuilt on
// every render.
const IMPORT_MODE_OPTIONS: KuikaRadioGroupOption[] = [
  {
    value: "insert",
    label: (
      <span>
        <strong>Insert</strong>
        <span className="kuika-table-editor__import-mode-hint"> · every row becomes a new row</span>
      </span>
    )
  },
  {
    value: "upsert",
    label: (
      <span>
        <strong>Update existing</strong>
        <span className="kuika-table-editor__import-mode-hint">{" · "}match by primary key, insert if missing</span>
      </span>
    )
  }
];

export interface IImportRowsModalProps {
  isOpen: boolean;
  tableName: string;
  tableSchema?: string;
  // When true, the modal exposes the "Insert / Update existing" toggle.
  // Pass false for tables without a primary key — upsert is meaningless
  // there and the server rejects the request anyway.
  hasPrimaryKey: boolean;
  onClose: () => void;
  onImported: (result: IImportSummary) => void;
}

const ImportRowsModal: React.FC<IImportRowsModalProps> = (props: IImportRowsModalProps): JSX.Element | null => {
  const { isOpen, tableName, tableSchema, hasPrimaryKey, onClose, onImported } = props;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<ITableEditorAlert | null>(null);
  const [summary, setSummary] = useState<IImportRowsResponse | null>(null);
  // "insert" — every row becomes an INSERT (default; works on every table).
  // "upsert" — primary-key match → UPDATE, no-match → INSERT. Only offered
  // when the table has a PK because the server rejects upsert otherwise.
  const [importMode, setImportMode] = useState<ImportMode>("insert");
  // Dry-run mode — server parses + validates + builds every row but
  // skips the actual write. Used as a preview affordance: pick file,
  // check "Dry run", see exactly what would happen (including per-row
  // failures), then uncheck and re-upload to commit. We keep the file
  // around after a dry-run so the user can hit "Commit" without
  // re-picking it; that runs the same import with dryRun=false.
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [lastFile, setLastFile] = useState<File | null>(null);
  // True when the displayed summary came from a dry-run upload — the
  // footer surfaces a "Commit for real" affordance in that state.
  // Tracked separately from `dryRun` because the user might toggle the
  // checkbox after the upload returns; the summary's provenance is
  // what matters for the action.
  const [summaryIsDryRun, setSummaryIsDryRun] = useState<boolean>(false);
  // Track the mode the displayed summary came from so the label can
  // say "N saved" on upsert (where the count is insert + update
  // combined — the DB path doesn't separate them on a per-row basis)
  // instead of "N inserted" which is wrong for the upsert case.
  const [summaryMode, setSummaryMode] = useState<ImportMode>("insert");
  // Reset to safe defaults whenever the modal reopens or the table
  // swaps. Without this, opening the modal on a PK-less table after
  // using upsert on a different table would silently send mode=upsert,
  // and a dry-run preference would leak across sessions.
  useEffect((): void => {
    if (isOpen) {
      setImportMode("insert");
      setDryRun(false);
      setLastFile(null);
      setSummaryIsDryRun(false);
      setSummaryMode("insert");
    }
  }, [isOpen, tableName, tableSchema]);
  // AbortController per in-flight upload. Reset on every new upload AND on
  // explicit cancel. Held in a ref because we never need a re-render when
  // the controller changes — only when the abort happens.
  const abortRef = useRef<AbortController | null>(null);
  // Tracks the component's mounted state so post-await setState calls
  // can short-circuit if the user closed the modal mid-upload. Refs over
  // state because we read it inside async handlers without re-rendering.
  const mountedRef = useRef<boolean>(true);
  useEffect((): (() => void) => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = (): void => {
    setFileName("");
    setError(null);
    setSummary(null);
  };

  const inferFormat = (name: string): "csv" | "xlsx" | null => {
    const lower = name.toLowerCase();
    if (lower.endsWith(".csv")) {
      return "csv";
    }
    if (lower.endsWith(".xlsx") || lower.endsWith(".xlsm")) {
      return "xlsx";
    }
    return null;
  };

  // overrideDryRun lets the in-modal "Commit" affordance re-run the
  // SAME file with dryRun=false without round-tripping through the
  // file input. Defaults to the current toggle state when omitted.
  const handleFile = async (file: File, overrideDryRun?: boolean): Promise<void> => {
    const format = inferFormat(file.name);
    if (!format) {
      setError(
        createTableEditorAlert({
          tone: "warn",
          operation: "import",
          title: "Unsupported import file",
          message: "Pick a .csv, .xlsx, or .xlsm file with a header row.",
          nextSteps: ["Export a template from this table if you need the expected column names."]
        })
      );
      return;
    }
    const effectiveDryRun: boolean = overrideDryRun ?? dryRun;
    // Replace any previous controller — the picker can fire a second time
    // before the first request resolves (rare, but possible).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsUploading(true);
    setError(null);
    setSummary(null);
    setFileName(file.name);
    setLastFile(file);
    try {
      const response = await TableEditorService.ImportRows(
        tableName,
        file,
        format,
        tableSchema,
        importMode,
        effectiveDryRun,
        controller.signal
      );
      // Abort came in between resolution and here — drop the result so we
      // don't fire setState on an unmounted / closed modal.
      if (controller.signal.aborted || !mountedRef.current) {
        return;
      }
      setSummary(response.data);
      setSummaryIsDryRun(effectiveDryRun);
      setSummaryMode(importMode);
      // Don't notify the parent on a dry-run — onImported triggers a
      // grid refresh + notice banner, and there's nothing to show
      // because nothing was committed.
      if (!effectiveDryRun) {
        onImported({
          inserted: response.data.inserted,
          failed: response.data.failed,
          warnings: response.data.warnings ?? []
        });
      }
    } catch (e: unknown) {
      // Distinguish user-initiated abort from real errors — aborting isn't
      // a failure, so we shouldn't surface it as one.
      if (axios.isCancel(e) || controller.signal.aborted) {
        return;
      }
      if (!mountedRef.current) {
        return;
      }
      const alert: ITableEditorAlert = await buildTableEditorErrorAlert(e, {
        operation: "import",
        title: "Import failed",
        fallbackMessage: "The file could not be imported."
      });
      setError(alert);
    } finally {
      if (mountedRef.current && abortRef.current === controller) {
        setIsUploading(false);
        abortRef.current = null;
      }
    }
  };

  const onPick = (): void => {
    // Guard against re-entry while an upload is in flight. The
    // dropzone's outer click handler and the inner Choose-file
    // button both route here; without this guard, clicking the
    // dropzone surface during upload would still open the OS
    // picker even though the inner button is disabled.
    if (isUploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) {
      void handleFile(file);
    }
  };

  // Cancel-and-close. While uploading this aborts the in-flight request
  // first so the response handler can no-op cleanly. The modal X close
  // (top-right) routes here too so there's no path that leaves a request
  // racing against an unmounted modal.
  const onCloseAndReset = (): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsUploading(false);
    reset();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  // Kicker + title + pills mirror the Expand Cell modal. The pills track
  // the most useful at-a-glance facts: current mode (insert / update) and
  // dry-run flag while picking, then the actual outcome counters after
  // the server returns. Keeps the header informative without forcing the
  // user to scan the body to confirm what mode they're in.
  const headerPills: JSX.Element[] = [];
  if (summary) {
    headerPills.push(
      <span key="inserted" className="kuika-table-editor__cell-expander-pill">
        {`${summary.inserted.toLocaleString("en-US")} ${summaryVerb(summaryMode, summaryIsDryRun)}`}
      </span>
    );
    if (summary.failed > 0) {
      headerPills.push(
        <span key="failed" className="kuika-table-editor__cell-expander-pill">
          {`${summary.failed.toLocaleString("en-US")} ${summaryIsDryRun ? "would fail" : "failed"}`}
        </span>
      );
    }
    if (summaryIsDryRun) {
      headerPills.push(
        <span key="dryrun" className="kuika-table-editor__cell-expander-pill">
          dry run
        </span>
      );
    }
  } else {
    headerPills.push(
      <span key="mode" className="kuika-table-editor__cell-expander-pill">
        {importMode === "upsert" ? "insert or update" : "insert only"}
      </span>
    );
    if (dryRun) {
      headerPills.push(
        <span key="dryrun" className="kuika-table-editor__cell-expander-pill">
          dry run
        </span>
      );
    }
  }

  const cancelLabel: string = isUploading ? "Cancel upload" : summary ? "Close" : "Cancel";
  return (
    <KModal
      showModal={isOpen}
      closeModal={onCloseAndReset}
      header={
        <span className="kuika-table-editor__cell-expander-header">
          <span className="kuika-table-editor__cell-expander-header-kicker" aria-hidden="true">
            Import rows
          </span>
          <span className="kuika-table-editor__cell-expander-header-title">{tableName}</span>
          <span className="kuika-table-editor__cell-expander-meta-pills">{headerPills}</span>
        </span>
      }
      width={520}
      footer={
        // Hand-rolled rather than the shared KModalFooter: the Commit
        // affordance only exists after a dry-run summary, so this
        // footer doesn't always have a primary action for
        // KModalFooter's mandatory submit slot to bind to. Markup
        // mirrors KModalFooter's own (`k-modal-footer__row` / `__start`
        // / `__end`) so the two look identical.
        <div className="k-modal-footer__row">
          <span className="k-modal-footer__start">
            <span className="kuika-table-editor__import-help">
              Large files are handled gracefully — they don't need to load into your browser.
            </span>
          </span>
          <span className="k-modal-footer__end">
            {summary && summaryIsDryRun && lastFile && (
              <KuikaButton
                type="primary"
                size="md"
                uppercase
                loading={isUploading}
                disabled={isUploading}
                onClick={(): void => {
                  // Re-run the same file with dryRun=false. Cached
                  // on lastFile so the user doesn't have to
                  // re-pick — the dry-run was their preview, this
                  // is the commit.
                  void handleFile(lastFile, false);
                }}
              >
                {`Commit ${summary.inserted.toLocaleString("en-US")} for real`}
              </KuikaButton>
            )}
            <KuikaButton
              type="tertiary"
              color="neutral"
              size="md"
              uppercase
              data-testid="tableEditorImportCloseButton"
              onClick={onCloseAndReset}
            >
              {cancelLabel}
            </KuikaButton>
          </span>
        </div>
      }
    >
      <div className="kuika-table-editor__import-modal">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xlsm"
          className="kuika-table-editor__import-file-input"
          data-testid="tableEditorImportFileInput"
          onChange={onFileInput}
        />

        {!summary && hasPrimaryKey && (
          <div className="kuika-table-editor__import-mode">
            <KuikaRadioGroup
              align="top"
              options={IMPORT_MODE_OPTIONS}
              value={importMode}
              disabled={isUploading}
              onChange={(value: string | number): void => setImportMode(value as ImportMode)}
            />
          </div>
        )}

        {!summary && (
          <label
            className="kuika-table-editor__import-dryrun"
            title="Preview which rows would import. Nothing is saved until you commit, and the final save still checks every rule on your table."
          >
            <KuikaCheckbox
              checked={dryRun}
              disabled={isUploading}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setDryRun(e.target.checked)}
              label={
                <>
                  <strong>Dry run</strong>
                  <span className="kuika-table-editor__import-mode-hint">{" · "}preview only · nothing is saved</span>
                </>
              }
            />
          </label>
        )}

        {/* The dropzone div is a visual surface — the inner KuikaButton
            is the one interactive element (proper native button,
            handles keyboard / focus / aria automatically). The outer
            click is a convenience: clicking ANYWHERE on the surface
            routes to onPick. We deliberately do NOT give the outer
            div role="button" / tabIndex={0} — that would create a
            second focus target overlapping the real button, plus
            invalid nested interactive UI per ARIA. The isUploading
            guard in onPick keeps the outer click safe while the inner
            button is in its disabled state. */}
        {!summary && (
          <div className="kuika-table-editor__import-dropzone" onClick={onPick}>
            <span className="kuika-table-editor__import-dropzone-title">Pick a file to import</span>
            <span className="kuika-table-editor__import-dropzone-sub">CSV or Excel (.xlsx) · header row required</span>
            <KuikaButton
              type="primary"
              size="sm"
              onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                // Stop the outer dropzone's onClick from firing a second
                // time (which `onPick`'s upload guard would no-op
                // anyway, but the double-fire is wasted work + would
                // trip any future side-effect added to onPick).
                e.stopPropagation();
                onPick();
              }}
              loading={isUploading}
              disabled={isUploading}
            >
              {isUploading ? "Uploading…" : "Choose file"}
            </KuikaButton>
            {fileName && isUploading && <span className="kuika-table-editor__import-summary-info">{fileName}</span>}
          </div>
        )}

        {error && (
          <TableEditorAlert alert={error} testId="tableEditorImportAlert" onDismiss={(): void => setError(null)} />
        )}

        {summary && (
          <div className="kuika-table-editor__import-summary" data-testid="tableEditorImportSummary">
            <div className="kuika-table-editor__import-summary-line" data-testid="tableEditorImportSummaryLine">
              <strong>{summary.inserted.toLocaleString("en-US")}</strong> row{summary.inserted === 1 ? "" : "s"}{" "}
              {summaryVerb(summaryMode, summaryIsDryRun)}
              {summary.failed > 0 && (
                <>
                  {" · "}
                  <strong>{summary.failed.toLocaleString("en-US")}</strong>{" "}
                  {summaryIsDryRun ? "would fail validation" : "failed"}
                </>
              )}
            </div>
            {summary.warnings.map((w: string, i: number): JSX.Element => (
              <div key={`w-${i}`} className="kuika-table-editor__import-summary-warn">
                {w}
              </div>
            ))}
            {summary.errors.length > 0 && (
              <details className="kuika-table-editor__import-summary-info">
                <summary data-testid="tableEditorImportErrorToggle">
                  {summary.errors.length} row error{summary.errors.length === 1 ? "" : "s"} (first{" "}
                  {Math.min(20, summary.errors.length)} shown)
                </summary>
                <ul className="kuika-table-editor__import-error-list" data-testid="tableEditorImportErrorList">
                  {summary.errors.slice(0, 20).map((e: IImportRowError, i: number): JSX.Element => (
                    <li key={`e-${i}`} data-testid="tableEditorImportRowError">
                      Row {e.rowIndex}: {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </KModal>
  );
};

export default ImportRowsModal;
